import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { useRpc } from '../data/people.ts'
import {
  editorValue, failureSinceLastRun, parsePeriod, PERIOD_UNITS, periodDays, periodPieces, samePeriod, toPeriod,
  type PeriodUnit, type RetentionCategory, type RetentionOverview,
} from '../data/retention.ts'
import { formatLocal } from '../lib/time.ts'
import { BackLink, Banner, Field, NotAllowed, Pill } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'
import { RowLink } from '../ui/RowLink.tsx'
import { StepUpField, useSensitive, type Sensitive } from '../ui/sensitive.tsx'
import { NoFactorBanner } from './people/Factor.tsx'

type T = (k: string, o?: Record<string, unknown>) => string

/** "Conservation des données" (ARCHITECTURE 16.2): how long the app keeps each kind of record, for the direction (retention.manage; the overview itself is an audit.read
 *  read). Online only (api.retention_overview), nothing of it kept on the device. Each category shows its period, from when it runs, and whether it is still a
 *  proposal or validated (and when); the owner validates it, makes it a proposal again or changes it with retention.set_policy@1, an online command behind a fresh
 *  second-factor code (a shorter period erases older records the next night). The page then reads the overview again: the server's answer is the truth.
 *  M23 item 24: the page is the direction's (retention.manage, the code that shows its row in Réglages); the mechanic who opens it by URL gets the card and no 400. */
export function RetentionPage() {
  const { t, i18n } = useTranslation()
  const { org, site, can } = useClub()
  const mayChange = can('retention.manage')
  const overview = useRpc<RetentionOverview>('retention_overview', { p_org: org.org_id }, mayChange)
  const s = useSensitive()
  const [editing, setEditing] = useState<string | null>(null)
  const o = overview.data
  const tz = site.timezone

  async function send(c: RetentionCategory, period: string, validated: boolean, successKey: string): Promise<boolean> {
    const ok = await s.command('retention.set_policy@1', { category: c.category, period, validated }, successKey)
    if (ok) { setEditing(null); await overview.reload() }
    return Boolean(ok)
  }

  if (!mayChange) return <NotAllowed permission="retention.manage" />
  return (
    <section className="flex flex-col gap-5" data-testid="retention-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="history" className="size-7 text-brand dark:text-fairway" />{t('retention.title')}</h1>
      <p className="meta">{t('retention.help')}</p>
      <Banner tone="info">{t('retention.adviser')}</Banner>
      {!overview.online && <Banner tone="warn">{t('retention.offline')}</Banner>}
      {overview.error && <Banner tone="error"><span data-testid="retention-error">{overview.error === 'PERMISSION_DENIED' || overview.error === 'SCOPE_DENIED' ? t('retention.denied') : t('retention.failed')}</span></Banner>}
      <NoFactorBanner testId="retention-no-factor" />
      <StepUpField s={s} />
      {!o ? (overview.online && !overview.error && <p className="opacity-70">{t('common.loading')}</p>) : (
        <>
          <ul className="flex flex-col gap-3" data-testid="retention-list">
            {o.categories.map((c) => (
              <CategoryCard key={c.category} c={c} t={t} lang={i18n.language} tz={tz} s={s} mayChange={mayChange}
                editing={editing === c.category} onEdit={(on) => setEditing(on ? c.category : null)} send={send} />
            ))}
          </ul>
          <div className="card flex flex-col gap-1 text-sm" data-testid="retention-runs">
            <p>{o.last_run ? t('retention.lastRun', { at: formatLocal(o.last_run.at, tz) }) : t('retention.noRun')}</p>
            {o.files.due > 0 && <p data-testid="retention-files-due">{t('retention.filesDue', { count: o.files.due })}</p>}
            {o.files.deleted > 0 && <p>{t('retention.filesDeleted', { count: o.files.deleted })}</p>}
          </div>
          {o.last_failure && (
            <Banner tone={failureSinceLastRun(o) ? 'warn' : 'info'}>
              <span data-testid="retention-failure">{t('retention.failure', { at: formatLocal(o.last_failure.at, tz), code: o.last_failure.code })}</span>
            </Banner>
          )}
        </>
      )}
      {/* M23 item 1: a 56-px row where a 21-px underlined text was */}
      <RowLink to="/privacy" icon="clipboard" tone="slate" title={t('retention.privacyLink')} testId="retention-privacy" />
    </section>
  )
}

function periodText(t: T, iso: string | null): string {
  const p = parsePeriod(iso)
  if (!p) return iso ?? '—'
  return periodPieces(p).map((x) => t(`retention.unit.${x.unit}`, { count: x.count })).join(' ')
}

function CategoryCard({ c, t, lang, tz, s, mayChange, editing, onEdit, send }: {
  c: RetentionCategory; t: T; lang: string; tz: string; s: Sensitive; mayChange: boolean; editing: boolean; onEdit: (on: boolean) => void
  send: (c: RetentionCategory, period: string, validated: boolean, successKey: string) => Promise<boolean>
}) {
  const label = (lang.startsWith('en') ? c.label.en : c.label.fr) ?? c.label.fr ?? c.category
  const busy = s.busy || !s.online
  return (
    <li className="card flex flex-col gap-2" data-testid={`retention-${c.category}`}>
      {/* the pill under the title: beside it, a long category name is squeezed into a narrow column on a phone */}
      <div className="flex flex-col items-start gap-1">
        <h2 className="text-lg font-bold leading-snug">{label}</h2>
        {!c.policy ? <Pill tone="gray">{t('retention.noPolicy')}</Pill>
          : c.validated_at ? <span data-testid="retention-validated"><Pill tone="green">{t('retention.validated', { date: formatLocal(c.validated_at, tz, 'dd/MM/yyyy') })}</Pill></span>
          : <span data-testid="retention-proposal"><Pill tone="amber">{t('retention.proposal')}</Pill></span>}
      </div>
      {c.policy && (
        <>
          <p><span className="font-semibold" data-testid="retention-period">{t('retention.period', { period: periodText(t, c.period) })}</span>{' '}<span className="opacity-80">{t(`retention.from.${c.measured_from}`)}</span></p>
          {c.basis && <p className="meta">{t('retention.basis', { basis: c.basis })}</p>}
          {/* M15-C: the five-year floor of the treatment register, a trigger on the server (RETENTION_BELOW_LEGAL_MINIMUM), "(à vérifier)" by the adviser */}
          {c.min_period && <p className="text-sm font-semibold" data-testid="retention-floor">{t('retention.floor', { period: periodText(t, c.min_period) })}</p>}
          {(c.due ?? 0) > 0 && <p className="text-sm" data-testid="retention-due">{t('retention.due', { count: c.due })}</p>}
          {c.kept_last_run > 0 && <p className="meta">{t('retention.kept', { count: c.kept_last_run })}</p>}
        </>
      )}
      {mayChange && c.policy && c.period && !editing && (
        <div className="flex flex-wrap gap-2">
          {c.validated_at
            ? <button className="btn" disabled={busy} onClick={() => void send(c, c.period!, false, 'retention.proposalDone')} data-testid="retention-unvalidate">{t('retention.unvalidate')}</button>
            : <button className="btn btn-primary" disabled={busy} onClick={() => void send(c, c.period!, true, 'retention.validatedDone')} data-testid="retention-validate">{t('retention.validate')}</button>}
          <button className="btn" disabled={busy} onClick={() => onEdit(true)} data-testid="retention-edit">{t('retention.edit')}</button>
        </div>
      )}
      {mayChange && editing && <PeriodEditor c={c} t={t} s={s} onCancel={() => onEdit(false)} send={send} />}
    </li>
  )
}

/** Mounted only once the overview is loaded (the parent passes the category in), so its first value is the period in force. */
function PeriodEditor({ c, t, s, onCancel, send }: {
  c: RetentionCategory; t: T; s: Sensitive; onCancel: () => void
  send: (c: RetentionCategory, period: string, validated: boolean, successKey: string) => Promise<boolean>
}) {
  const start = editorValue(parsePeriod(c.period))
  const [n, setN] = useState(start.n)
  const [unit, setUnit] = useState<PeriodUnit>(start.unit)
  const [validated, setValidated] = useState(true)
  const period = toPeriod(n, unit)
  const now = parsePeriod(c.period), next = parsePeriod(period)
  const shorter = now !== null && next !== null && periodDays(next) < periodDays(now)
  const unchanged = period !== null && samePeriod(period, c.period) && Boolean(c.validated_at) === validated
  const id = `ret-${c.category}`
  return (
    <form className="flex flex-col gap-3 rounded-xl border-2 border-slate-200 p-3 dark:border-slate-700" data-testid="retention-editor" onSubmit={(e) => {
      e.preventDefault()
      if (period) void send(c, period, validated, 'retention.savedDone')
    }}>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('retention.number')} htmlFor={`${id}-n`}>
          <input id={`${id}-n`} className="input" inputMode="numeric" value={n} onChange={(e) => setN(e.target.value)} data-testid="retention-n" />
        </Field>
        <Field label={t('retention.unitLabel')} htmlFor={`${id}-u`}>
          <select id={`${id}-u`} className="input" value={unit} onChange={(e) => setUnit(e.target.value as PeriodUnit)} data-testid="retention-unit">
            {PERIOD_UNITS.map((u) => <option key={u} value={u}>{t(`retention.unitName.${u}`)}</option>)}
          </select>
        </Field>
      </div>
      {n.trim() !== '' && !period && <p className="text-sm text-red-700 dark:text-red-300" data-testid="retention-invalid">{t('retention.invalid')}</p>}
      {shorter && <Banner tone="warn"><span data-testid="retention-shorter">{t('retention.shorter')}</span></Banner>}
      <label className="flex min-h-14 items-center gap-3">
        <input type="checkbox" className="size-6" checked={validated} onChange={(e) => setValidated(e.target.checked)} data-testid="retention-validated-box" />
        <span>{t('retention.validateToo')}</span>
      </label>
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary" disabled={s.busy || !s.online || !period || unchanged} data-testid="retention-save">{t('retention.save')}</button>
        <button type="button" className="btn" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  )
}
